<template>
    <div class="container-fluid">
        <!-- LOADING -->
        <Loading v-if="!loaded"></Loading>
        <!-- LOADED -->
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('medication_list') }}</h3>
            </div>
            <!-- FILTER (VNR / NAME) -->
            <div class="form-group row mt-4">
                <div class="col-md-4">
                    <label for="vnrName">{{ `${$t('filter')} (${$t('vnr')} / ${$t('name').toLowerCase()})` }}</label>
                    <input
                        id="vnrName"
                        type="text"
                        class="form-control"
                        v-on:keyup="filterMedications"
                        v-model="searchName"
                    />
                </div>
            </div>
            <!-- TABLE: MEDICATION LIST -->
            <div class="row mt-4">
                <div class="col">
                    <b-table
                        class="mt-4"
                        striped
                        hover
                        :fields="tableFields"
                        :items="filteredMedications"
                        sort-by="vnr"
                    >
                        <!-- HEADER ROW TOOLTIPS -->
                        <template v-slot:head()="data">
                            <span v-b-tooltip.hover :title="data.field.tooltip">
                                {{ data.label }}
                            </span>
                        </template>
                        <!-- VNR (CLICK TO SHOW DRUG CARD) -->
                        <template #cell(vnr)="data">
                            <router-link :to="{ name: 'drug_card.index', params: { vnr: data.item.vnr } }">
                                {{ data.item.vnr }}
                            </router-link>
                        </template>
                        <!-- NAME (CLICK TO SHOW DRUG CARD) -->
                        <template #cell(medication_name)="data">
                            <router-link :to="{ name: 'drug_card.index', params: { vnr: data.item.vnr } }">
                                {{ getMedDisplayName({ ...data.item, name: data.item.medication_name }, false) }}
                            </router-link>
                        </template>
                        <!-- DEFAULT LOCKER -->
                        <template #cell(default_locker)="data">
                            <b-form-select
                                v-model="data.item.default_locker"
                                :options="pharmacyLockerOptions"
                                @change="setDefaultLocker($event, data.item.vnr)"
                            />
                        </template>
                        <!-- LOW-INVENTORY ALERT THRESHOLD -->
                        <template #cell(low_inventory_alert)="data">
                            <ValidationObserver v-slot="{ invalid }">
                                <ValidationProvider
                                    :name="`lowInventoryAlert_${data.item.vnr}`"
                                    :rules="{
                                        integer: true,
                                        positive: true
                                    }"
                                    v-slot="{ errors }"
                                >
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="data.item.low_inventory_alert_threshold"
                                        @keydown="
                                            if ($event.keyCode === 9) {
                                                handleLowInventoryAlertUpdate(
                                                    data.item.vnr,
                                                    data.item.low_inventory_alert_threshold,
                                                    invalid
                                                );
                                            }
                                        "
                                        @keyup="
                                            handleLowInventoryAlertUpdate(
                                                data.item.vnr,
                                                data.item.low_inventory_alert_threshold,
                                                invalid
                                            )
                                        "
                                    />
                                    <div class="invalid-feedback" style="display: block">
                                        <span>{{ $t(errors[0]) }}</span>
                                    </div>
                                </ValidationProvider>
                            </ValidationObserver>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import {
    getPharmacyConfig,
    getPharmacyLockers,
    getUsedMedications,
    handleAxiosError,
    updateDefaultLocker,
    updateLowInventoryAlert
} from '@/services/axiosService';
import { compareNumbers } from '@/services/numberService';
import { getMedDisplayName } from '@/services/medicationService';

export default {
    name: 'MedicationList',
    components: { Loading },
    data() {
        return {
            errors: [],
            filteredMedications: [],
            getMedDisplayName,
            loaded: false,
            lowInventoryAlertInitialValues: {},
            lowInventoryAlertUpdateDelay: 1000,
            lowInventoryAlertUpdateTimer: {},
            medications: [],
            pharmacyConfig: {},
            pharmacyLockers: [],
            searchName: '',
            searchNameLength: 0
        };
    },
    async created() {
        // get pharmacy's config, used medications and locker config
        await this.getPharmacyConfig();
        await this.getMedications();
        await this.getPharmacyLockers();
        this.loaded = true;
    },
    computed: {
        ...mapState('account', ['status', 'user']),
        pharmacyLockerOptions() {
            // options: "default locker not set" + separate option for each locker
            return [{ value: null, text: this.$t('default_locker_not_set') }, ...this.pharmacyLockers];
        },
        tableFields() {
            let tableFields = [
                { key: 'vnr', sortable: true, label: this.$t('vnr'), tdClass: 'align-middle' },
                { key: 'medication_name', sortable: true, label: this.$t('medication_name'), tdClass: 'align-middle' },
                {
                    ...(this.pharmacyConfig.default_vnr_locker_setup && {
                        key: 'default_locker',
                        sortable: true,
                        label: this.$t('default_locker'),
                        tdClass: 'align-middle'
                    })
                },
                {
                    key: 'low_inventory_alert',
                    sortable: true,
                    label: `${this.$t('low_inventory_alert_threshold')} (${this.$t(
                        'packages_short_singular_partitive'
                    )})`,
                    tdClass: 'align-middle',
                    tooltip: this.$t('low_inventory_alert_threshold_tooltip_medication_list') // include tooltip for low-inv alert threshold column
                }
            ];
            return tableFields;
        }
    },
    methods: {
        ...mapActions('account', ['logout']),
        // filter medications based on filter criteria
        filterMedications() {
            if (this.searchName.length >= 3) {
                this.loaded = false;
                let result = [],
                    key;
                for (key in this.medications) {
                    if (
                        this.medications.hasOwnProperty(key) &&
                        (this.medications[key].medication_name.toLowerCase().indexOf(this.searchName.toLowerCase()) >
                            -1 ||
                            this.medications[key].vnr.toLowerCase().indexOf(this.searchName.toLowerCase()) > -1)
                    ) {
                        result[key] = this.medications[key];
                    }
                }
                this.filteredMedications = result;
                this.searchNameLength = this.searchName.length;
                this.loaded = true;
            } else if (this.searchName.length < 3 && this.searchNameLength > this.searchName.length) {
                this.loaded = false;
                this.filteredMedications = this.medications;
                this.searchNameLength = 0;
                this.loaded = true;
            }
        },
        // get pharmacy's used medications (i.e. meds that have logs)
        async getMedications() {
            try {
                const medications = await getUsedMedications();
                this.medications = _.sortBy(medications, ['medication_name', 'vnr']);
                this.filteredMedications = this.medications;

                // store medications' initial low-inventory alert thresholds
                const lowInventoryAlertInitialValues = {};
                this.medications.map(med => {
                    lowInventoryAlertInitialValues[med.vnr] = med.low_inventory_alert_threshold;
                });
                this.lowInventoryAlertInitialValues = lowInventoryAlertInitialValues;
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // get pharmacy's config
        async getPharmacyConfig() {
            try {
                this.pharmacyConfig = await getPharmacyConfig(this.user.pharmacy_id);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // get pharmacy's locker config
        async getPharmacyLockers() {
            try {
                const pharmacyLockers = await getPharmacyLockers();
                // store results in variable
                pharmacyLockers.map((cabinets, cabinetIndex) =>
                    cabinets.map((_lockers, lockerIndex) =>
                        this.pharmacyLockers.push({
                            value: `${cabinetIndex}-${lockerIndex}`,
                            text: `${cabinetIndex}-${lockerIndex}`
                        })
                    )
                );
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // handle low-inventory alert value update (incl. delay before update)
        async handleLowInventoryAlertUpdate(vnr, packs, invalid) {
            clearTimeout(this.lowInventoryAlertUpdateTimer[vnr]); // reset any remaining timer
            if (!invalid && !compareNumbers(this.lowInventoryAlertInitialValues[vnr], packs)) {
                // if field value is valid and different from previous value, update value (with delay)
                this.lowInventoryAlertUpdateTimer[vnr] = setTimeout(
                    async () => await this.updateLowInventoryAlert(vnr, packs),
                    this.lowInventoryAlertUpdateDelay
                );
            }
        },
        // set default locker
        async setDefaultLocker(locker, vnr) {
            const data = {
                locker,
                vnr,
                webtool_action: true
            };
            try {
                await updateDefaultLocker(data);
                this.$toastr.s(this.$t('default_locker_set'));
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // update low-intentory alert value
        async updateLowInventoryAlert(vnr, packs) {
            try {
                const data = {
                    amount_unit: 'packages',
                    amount_value: packs,
                    vnr
                };
                await updateLowInventoryAlert(this.user.pharmacy_id, data);
                this.$toastr.s(this.$t('low_inventory_alert_threshold_set'));
                this.lowInventoryAlertInitialValues[vnr] = packs; // set initial value to updated value
            } catch (error) {
                handleAxiosError(error);
            }
        }
    }
};
</script>

<style scoped>
ul {
    columns: 2;
    -webkit-columns: 2;
    -moz-columns: 2;
}
</style>
